/**
 * The register's cart — what a cashier has rung up but not yet committed.
 *
 * It is a plain module with no React in it, for the same reason `registerFlow`
 * is: the two questions a cart gets wrong are *what merges with what* and *how
 * much is it*, and neither can be proved by rendering a component.
 *
 * ## Money
 *
 * Every price here is the API's fixed-scale decimal **string**, and every sum
 * goes through `toMinor`/`fromMinor` from `./lib` — integer minor units in the
 * currency the amount is actually denominated in. Nothing calls `Number()` on a
 * price and nothing re-parses a formatted string: `formatCurrency` is a one-way
 * door, and reading a total back out of "SAR 12.50" is how a till and a bill
 * start disagreeing. "Minor unit" is not "cents" either — KWD has three of them
 * and JPY none — which is why the currency code is an argument everywhere below
 * rather than an assumed two decimal places.
 *
 * These figures are display-only regardless. §38 / ADR-003: the client shows
 * what it read, the server prices what it charges, and {@link orderLines} — the
 * only thing here that reaches a request body — carries no price at all.
 *
 * ## What merges
 *
 * A cart line is a *configuration*, not a product. A large latte with an extra
 * shot and "no sugar" written on it is not the same thing to make, or to charge
 * for, as a small latte — so {@link lineKey} is the item, its variant, its
 * chosen modifiers and its note, and two rings merge only when all four agree.
 *
 * This is deliberately stricter than the POS register, which merges by item id
 * alone and sends every line with no variant and no modifiers
 * (`pos-api.ts: isSimpleProduct` documents that gap). The dashboard can be
 * stricter because it asks the question — `ItemConfigDialog` — before the line
 * exists.
 */
import type { OrderLineInput } from '@/lib/mutations';
import { fromMinor, toMinor } from './lib';

/** One chosen option out of a modifier group, with the price it adds. */
export interface CartModifier {
  modifierId: string;
  name: string;
  /** The API's decimal string for `Modifier.price`. Display only (§38). */
  price: string;
}

export interface CartLine {
  /** {@link lineKey} — React's identity for the row *and* the merge rule. */
  key: string;
  itemId: string;
  /** What the cashier sees: the localized product name, plus its size. */
  name: string;
  variantId: string | null;
  unitPrice: string;
  quantity: number;
  /** "no ice", "allergy — nuts". Part of the identity: it changes the plate. */
  notes: string;
  modifiers: CartModifier[];
}

/** What a line is made of, before it has a key or a quantity. */
export interface LineDraft {
  itemId: string;
  name: string;
  variantId?: string | null;
  unitPrice: string;
  quantity?: number;
  notes?: string;
  modifiers?: CartModifier[];
}

/**
 * The identity of one configuration.
 *
 * Modifier ids are sorted, so picking "extra shot, oat milk" and "oat milk,
 * extra shot" are one line rather than two identical-looking ones the cashier
 * then has to reconcile by eye. The note is trimmed but not case-folded: it is
 * free text a cook reads, and "NO NUTS" is worth keeping distinct from a
 * casually typed "no nuts" rather than silently merging one into the other.
 */
export function lineKey(draft: Pick<LineDraft, 'itemId' | 'variantId' | 'notes' | 'modifiers'>): string {
  const modifiers = (draft.modifiers ?? [])
    .map((modifier) => modifier.modifierId)
    .slice()
    .sort()
    .join(',');
  return [draft.itemId, draft.variantId ?? '', modifiers, (draft.notes ?? '').trim()].join('|');
}

/**
 * What one of this configuration costs: the item's base, plus the size's delta,
 * plus every chosen modifier.
 *
 * `Variant.priceDelta` is a signed decimal — a small size is a negative delta —
 * so this is addition, not a maximum, and it is done in minor units for the
 * usual reason: 3.30 + 0.60 must be 3.90 and not 3.9000000000000004.
 *
 * The result is still a label. The server re-prices every line from the catalog
 * and ignores anything a client sends, so a stale menu can be wrong on this
 * screen and can never be wrong on the bill.
 */
export function unitPriceOf(
  basePrice: string,
  variantDelta: string | null | undefined,
  modifiers: CartModifier[],
  currency: string,
): string {
  let minor = toMinor(basePrice, currency) + toMinor(variantDelta, currency);
  for (const modifier of modifiers) minor += toMinor(modifier.price, currency);
  return fromMinor(minor, currency);
}

/** Ring one configuration up. An identical one already in the cart gains a unit. */
export function addLine(cart: CartLine[], draft: LineDraft): CartLine[] {
  const key = lineKey(draft);
  const quantity = draft.quantity ?? 1;
  const existing = cart.find((line) => line.key === key);
  if (existing) {
    return cart.map((line) =>
      line.key === key ? { ...line, quantity: line.quantity + quantity } : line,
    );
  }
  return [
    ...cart,
    {
      key,
      itemId: draft.itemId,
      name: draft.name,
      variantId: draft.variantId ?? null,
      unitPrice: draft.unitPrice,
      quantity,
      notes: (draft.notes ?? '').trim(),
      modifiers: draft.modifiers ?? [],
    },
  ];
}

/** `+`/`−` on a line. Reaching zero removes it — there is no zero-quantity line. */
export function changeQuantity(cart: CartLine[], key: string, delta: number): CartLine[] {
  return cart
    .map((line) => (line.key === key ? { ...line, quantity: line.quantity + delta } : line))
    .filter((line) => line.quantity > 0);
}

export function removeLine(cart: CartLine[], key: string): CartLine[] {
  return cart.filter((line) => line.key !== key);
}

/**
 * Write a note onto a line that is already in the cart.
 *
 * The note is part of the identity, so this re-keys the line — and if the new
 * key collides with another line the cashier already rang up, the two are the
 * same thing to make and are merged rather than left as two rows that will
 * arrive at the pass as one plate each.
 */
export function setNotes(cart: CartLine[], key: string, notes: string): CartLine[] {
  const target = cart.find((line) => line.key === key);
  if (!target) return cart;
  const moved: CartLine = { ...target, notes: notes.trim() };
  moved.key = lineKey(moved);
  if (moved.key === key) return cart.map((line) => (line.key === key ? moved : line));

  const twin = cart.find((line) => line.key === moved.key);
  if (!twin) return cart.map((line) => (line.key === key ? moved : line));
  return cart
    .filter((line) => line.key !== key)
    .map((line) =>
      line.key === moved.key ? { ...line, quantity: line.quantity + moved.quantity } : line,
    );
}

/** One line's money: unit price × quantity, in minor units the whole way. */
export function lineTotal(line: CartLine, currency: string): string {
  return fromMinor(toMinor(line.unitPrice, currency) * line.quantity, currency);
}

/**
 * The cart's subtotal — and it is a *subtotal*, not the total the guest pays.
 * Tax and any service charge are applied server-side, so a screen that called
 * this "total" would be quoting a figure the printed bill disagrees with.
 */
export function cartTotal(cart: CartLine[], currency: string): string {
  let minor = 0;
  for (const line of cart) minor += toMinor(line.unitPrice, currency) * line.quantity;
  return fromMinor(minor, currency);
}

/** How many units are in the cart, for the badge on a tile and the send button. */
export function cartCount(cart: CartLine[]): number {
  let count = 0;
  for (const line of cart) count += line.quantity;
  return count;
}

/**
 * The cart as `OrderLineDto[]`.
 *
 * No price, no name, no total — the DTO declares none of them and the API's
 * `forbidNonWhitelisted` pipe answers 400 to a field it has not declared, so an
 * extra key here would not be a harmless extra: it would dead-letter every
 * queued order on this till at once. Empty optionals are omitted rather than
 * sent as null for the same reason.
 */
export function orderLines(cart: CartLine[]): OrderLineInput[] {
  return cart.map((line) => ({
    itemId: line.itemId,
    quantity: line.quantity,
    ...(line.variantId ? { variantId: line.variantId } : {}),
    ...(line.notes ? { notes: line.notes } : {}),
    ...(line.modifiers.length
      ? { modifiers: line.modifiers.map((modifier) => ({ modifierId: modifier.modifierId })) }
      : {}),
  }));
}
